import {inject, Injectable} from '@angular/core';
import {
  FormArray,
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  AdditionalProcessor,
  Condition,
  CONDITION_FORM_BUILDER,
  ConditionalStatement,
  ConditionalStatementList,
  DestinationAttribute,
  MappingEngine,
  MappingRule,
  MappingRuleList,
  MappingRuleType,
} from '@em/shared/mapping-engine/domain-models';
import {Observable, of} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {additionalProcessorValidator} from '../../validators/additional-process.validator';
import {mappingRuleValidator} from '../../validators/mapping-rule.validator';
import {onlyNewCustomAttribute} from '../../validators/only-new-custom-attribute.validator';
import {uniqueRuleListAttribute} from '../../validators/unique-rule-list-attribute.validator';

@Injectable()
export class MappingEngineFormBuilderService<T = Condition> {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _conditionFormBuilder = inject(CONDITION_FORM_BUILDER);

  private _mappingEngineForm: UntypedFormGroup | undefined;
  private _destinationAttributes: DestinationAttribute[] = [];

  updateDestinationAttributes(destinationAttributes: DestinationAttribute[]) {
    this._destinationAttributes = destinationAttributes;
  }

  buildMappingEngineForm(
    mappingEngine: MappingEngine<T>,
    destinationAttributes: DestinationAttribute[] = [],
  ): UntypedFormGroup {
    this._destinationAttributes = destinationAttributes;
    this._mappingEngineForm = this._fb.group({
      mappingRuleList: this._fb.array([]),
    });

    if (mappingEngine?.mappingRuleList?.length) {
      mappingEngine?.mappingRuleList?.forEach((mappingRuleList) => {
        const ruleListFormArray = this._mappingEngineForm?.get(
          'mappingRuleList',
        ) as UntypedFormArray;
        ruleListFormArray.push(
          this.buildMappingRuleList(mappingRuleList, ruleListFormArray),
        );
      });
    }

    return this._mappingEngineForm;
  }

  buildMappingRuleList(
    mappingRuleList: MappingRuleList<T>,
    mappingRuleListFormArray: FormArray,
  ): UntypedFormGroup {
    const mappingRuleArray = this._fb.array([]);

    if (mappingRuleList?.mappingRules?.length) {
      mappingRuleList?.mappingRules.forEach((mappingRule) =>
        mappingRuleArray.push(this.buildMappingRule(mappingRule)),
      );
    }

    return this._fb.group({
      attribute: [
        mappingRuleList.attribute || '',
        [
          Validators.required,
          uniqueRuleListAttribute(mappingRuleListFormArray),
        ].concat(
          mappingRuleList.custom
            ? [
                onlyNewCustomAttribute(
                  this._destinationAttributes.map((attr) => attr.attribute),
                ),
              ]
            : [],
        ),
      ],
      stripHTML: mappingRuleList.stripHTML,
      mappingRules: mappingRuleArray,
      custom: mappingRuleList.custom,
    });
  }

  buildMappingRule(mappingRule: MappingRule<T>): UntypedFormGroup {
    const defaultType: MappingRuleType = 'attribute';
    return this._fb.group(
      {
        mappingType: [
          mappingRule.mappingType || defaultType,
          Validators.required,
        ],
        conditionalStatementList: this.buildConditionalStatementList(
          mappingRule.conditionalStatementList,
        ),
        mappingValue: [
          mappingRule.mappingValue,
          mappingRule.mappingType === 'leave_empty'
            ? []
            : [Validators.required],
        ],
        additionalProcessor: this.buildAdditionalProcessor(
          mappingRule.additionalProcessor,
        ),
      },
      {validators: [mappingRuleValidator()]},
    );
  }

  buildConditionalStatementList(
    conditionalStatementList: ConditionalStatementList<T> | undefined,
  ) {
    const conditionalStatementsArray = this._fb.array([]);

    if (conditionalStatementList?.conditionalStatements.length) {
      conditionalStatementList?.conditionalStatements.forEach((statement) =>
        conditionalStatementsArray.push(
          this.buildConditnalStatement(statement),
        ),
      );
    }
    return this._fb.group({
      logicalOperator: [conditionalStatementList?.logicalOperator || null],
      conditionalStatements: conditionalStatementsArray,
    });
  }

  buildConditnalStatement(conditionalStatement: ConditionalStatement<T>) {
    const conditionsArray = this._fb.array([]);

    if (conditionalStatement?.conditions.length) {
      conditionalStatement?.conditions.forEach((condition) =>
        conditionsArray.push(
          this._conditionFormBuilder.buildCondition(condition),
        ),
      );
    }

    return this._fb.group({
      logicalOperator: [conditionalStatement.logicalOperator || null],
      conditions: conditionsArray,
    });
  }

  buildAdditionalProcessor(additinoalProcessor: AdditionalProcessor) {
    const formGroup = this._fb.group(
      {},
      {validators: additionalProcessorValidator()},
    );

    for (const key in additinoalProcessor) {
      if (Object.prototype.hasOwnProperty.call(additinoalProcessor, key)) {
        const value = additinoalProcessor[key as keyof AdditionalProcessor];
        formGroup.addControl(key, new FormControl(value));
      }
    }
    return formGroup;
  }

  addNewConditionalStatement(listFormArray: UntypedFormArray) {
    listFormArray.push(
      this.buildConditnalStatement({
        logicalOperator: null,
        conditions: [{} as T],
      }),
    );
  }

  addNewConditionalStatementList(
    mappingRuleForm: UntypedFormGroup,
    conditions?: T[],
  ) {
    (
      mappingRuleForm
        .get('conditionalStatementList')
        ?.get('conditionalStatements') as UntypedFormArray
    )?.push(
      this.buildConditnalStatement({
        conditions: conditions || [{} as T],
        logicalOperator: null,
      }),
    );
  }

  addNewMappingRuleList(custom: boolean): UntypedFormGroup {
    const ruleListFormArray = this._mappingEngineForm?.get(
      'mappingRuleList',
    ) as UntypedFormArray;
    const newGroup = this.buildMappingRuleList(
      {
        attribute: '',
        mappingRules: [{mappingType: 'attribute', additionalProcessor: {}}],
        stripHTML: false,
        custom,
      },
      ruleListFormArray,
    );
    ruleListFormArray.push(newGroup);
    return newGroup;
  }

  addNewMappingRuleListWithValues(
    custom: boolean,
    attribute: string,
    mappingRules: Array<MappingRule<T>>,
    atIndex?: number,
  ) {
    const ruleListFormArray = this._mappingEngineForm?.get(
      'mappingRuleList',
    ) as UntypedFormArray;

    const newGroup = this.buildMappingRuleList(
      {
        attribute,
        mappingRules,
        stripHTML: false,
        custom,
      },
      ruleListFormArray,
    );
    if (atIndex !== undefined) {
      ruleListFormArray.insert(atIndex, newGroup);
    } else {
      ruleListFormArray.push(newGroup);
    }
  }

  addMappingRule(
    mappingRule: MappingRule<T>,
    ruleListFormArray: UntypedFormArray,
    index?: number,
  ) {
    if (!index) {
      ruleListFormArray.push(this.buildMappingRule(mappingRule));
    } else {
      ruleListFormArray.insert(index, this.buildMappingRule(mappingRule));
    }
  }

  addCondition(condition: Condition, conditionsFormArray: UntypedFormArray) {
    conditionsFormArray.push(
      this._conditionFormBuilder.buildCondition(condition),
    );
  }

  reamoveAllExpceptFirst(mappingRuleArray: UntypedFormArray) {
    if (!mappingRuleArray.controls.length) {
      return;
    }

    while (mappingRuleArray.controls.length > 1) {
      mappingRuleArray.removeAt(mappingRuleArray.controls.length - 1);
    }
  }

  clearAllConditions(mappinRuleForm: UntypedFormGroup) {
    (
      mappinRuleForm
        .get('conditionalStatementList')
        ?.get('conditionalStatements') as UntypedFormArray
    )?.clear();
    mappinRuleForm
      .get('conditionalStatementList')
      ?.get('logicalOperator')
      ?.setValue(null);
  }

  removeMappingRuleList(index: number) {
    (
      this._mappingEngineForm?.get('mappingRuleList') as UntypedFormArray
    ).removeAt(index);
  }

  getAllMappingRuleLists(): UntypedFormArray {
    return this._mappingEngineForm?.get('mappingRuleList') as UntypedFormArray;
  }

  getMappingRuleList(index: number): UntypedFormGroup {
    return (this._mappingEngineForm?.get('mappingRuleList') as UntypedFormArray)
      .controls[index] as UntypedFormGroup;
  }

  getMappingRule(
    index: number,
    ruleListGroup: UntypedFormGroup,
  ): UntypedFormGroup {
    return (ruleListGroup.get('mappingRules') as UntypedFormArray).controls[
      index
    ] as UntypedFormGroup;
  }

  getCondition(
    index: number,
    ruleListGroup: UntypedFormGroup,
  ): UntypedFormGroup {
    return (ruleListGroup.get('conditions') as UntypedFormArray).controls[
      index
    ] as UntypedFormGroup;
  }

  getConditionalStatement(
    index: number,
    statementListGroup: UntypedFormGroup,
  ): UntypedFormGroup {
    return (statementListGroup.get('conditionalStatements') as UntypedFormArray)
      .controls[index] as UntypedFormGroup;
  }

  getAllAddedAttributes(): Observable<string[]> {
    if (this._mappingEngineForm) {
      return this._mappingEngineForm?.valueChanges.pipe(
        startWith(this._mappingEngineForm?.value),
        map((mappingEngine: MappingEngine) =>
          mappingEngine.mappingRuleList.map((ruleList) => ruleList.attribute),
        ),
      );
    }
    return of([]);
  }
}
